import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { Trans, useLingui } from "@lingui/react/macro"
import type {
  DealDrawer as DealDrawerData,
  DealDrawerRosterContact,
} from "@/types/v2/conversation"
import { useDealContacts } from "@/hooks/use-deal-contacts"
import type { DealPropKey } from "@/lib/deal-prop-update"

// Manage contacts picker opened from the roster "+ Add" button
// or the contact card avatar (CON-260).
//
// Two variants from the same component:
//   - `popover` (desktop / tablet): floating panel anchored
//     under the trigger, rendered into a body portal.
//   - `sheet` (mobile): bottom sheet covering the lower half
//     of the screen, dimmed scrim above.
//
// Both share the same body: search input, ON THIS DEAL
// section (current roster rows with ⭐ primary + × remove),
// ADD FROM <Company> section (candidates from
// `deal.companyContacts`), and a disabled "Create new
// contact…" footer (deferred — CON-260 scope).

const POPOVER_WIDTH = 340

interface DealContactPickerProps {
  deal: DealDrawerData
  dealPropKey: DealPropKey
  open: boolean
  onOpenChange: (next: boolean) => void
  trigger: ReactNode
  triggerLabel: string
  variant: "popover" | "sheet"
}

export function DealContactPicker({
  deal,
  dealPropKey,
  open,
  onOpenChange,
  trigger,
  triggerLabel,
  variant,
}: DealContactPickerProps) {
  const triggerRef = useRef<HTMLElement | null>(null)
  const closePicker = () => onOpenChange(false)

  const { addContact, makePrimary, removeContact, submitting } =
    useDealContacts({ deal, dealPropKey })

  // Esc closes the picker.
  useEffect(() => {
    if (!open) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePicker()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Inject our ref into the consumer's trigger element.
  const triggerWithRef = useMemo(() => {
    if (!isValidElement(trigger)) return trigger
    return cloneElement(
      trigger as ReactElement<{
        ref?: (el: HTMLElement | null) => void
        "aria-label"?: string
      }>,
      {
        ref: (el: HTMLElement | null) => {
          triggerRef.current = el
        },
        "aria-label": triggerLabel,
      },
    )
  }, [trigger, triggerLabel])

  return (
    <>
      {triggerWithRef}
      {open ? (
        variant === "popover" ? (
          <PopoverPanel
            deal={deal}
            anchor={triggerRef.current}
            submitting={submitting}
            onAdd={addContact}
            onMakePrimary={makePrimary}
            onRemove={removeContact}
            onClose={closePicker}
          />
        ) : (
          <SheetPanel
            deal={deal}
            submitting={submitting}
            onAdd={addContact}
            onMakePrimary={makePrimary}
            onRemove={removeContact}
            onClose={closePicker}
          />
        )
      ) : null}
    </>
  )
}

interface PanelProps {
  deal: DealDrawerData
  submitting: boolean
  onAdd: (contactId: number, role?: string) => void
  onMakePrimary: (contactId: number) => void
  onRemove: (contactId: number) => void
  onClose: () => void
}

function PopoverPanel({
  deal,
  anchor,
  submitting,
  onAdd,
  onMakePrimary,
  onRemove,
  onClose,
}: PanelProps & { anchor: HTMLElement | null }) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (!anchor) return
    const measure = () => setRect(anchor.getBoundingClientRect())
    measure()
    window.addEventListener("resize", measure)
    window.addEventListener("scroll", measure, true)
    return () => {
      window.removeEventListener("resize", measure)
      window.removeEventListener("scroll", measure, true)
    }
  }, [anchor])

  useEffect(() => {
    const handle = (event: PointerEvent) => {
      const target = event.target as Node
      if (
        anchor?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return
      }
      onClose()
    }
    document.addEventListener("pointerdown", handle)
    return () => document.removeEventListener("pointerdown", handle)
  }, [anchor, onClose])

  const { t } = useLingui()

  if (typeof document === "undefined") return null

  const left = rect
    ? Math.max(
        8,
        Math.min(window.innerWidth - POPOVER_WIDTH - 8, rect.left),
      )
    : 8
  const top = rect ? rect.bottom + 8 : 8

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t`Manage contacts`}
      data-testid="contact-picker-popover"
      style={{
        position: "fixed",
        top,
        left,
        width: POPOVER_WIDTH,
        zIndex: 60,
      }}
      className="flex flex-col rounded-[14px] overflow-clip shadow-[0_14px_32px_rgba(30,27,23,0.14),0_2px_6px_rgba(30,27,23,0.06)] bg-white border border-[#E5DFD3]"
    >
      <PickerBody
        deal={deal}
        submitting={submitting}
        onAdd={onAdd}
        onMakePrimary={onMakePrimary}
        onRemove={onRemove}
      />
    </div>,
    document.body,
  )
}

function SheetPanel({
  deal,
  submitting,
  onAdd,
  onMakePrimary,
  onRemove,
  onClose,
}: PanelProps) {
  const { t } = useLingui()

  if (typeof document === "undefined") return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t`Manage contacts`}
      data-testid="contact-picker-sheet"
      className="fixed inset-0 z-50 flex flex-col justify-end"
    >
      <button
        type="button"
        aria-label={t`Close contact picker`}
        onClick={onClose}
        className="absolute inset-0 bg-[#1E1B17]/40"
      />
      <div className="relative flex flex-col rounded-t-2xl max-h-[80vh] bg-white shadow-[0_-12px_32px_rgba(30,27,23,0.18)]">
        <div className="flex items-center justify-center pt-2.5 pb-1">
          <span className="block h-1 w-9 rounded-full bg-[#E5DFD3]" />
        </div>
        <PickerBody
          deal={deal}
          submitting={submitting}
          onAdd={onAdd}
          onMakePrimary={onMakePrimary}
          onRemove={onRemove}
        />
      </div>
    </div>,
    document.body,
  )
}

// Body shared between popover and sheet.
function PickerBody({
  deal,
  submitting,
  onAdd,
  onMakePrimary,
  onRemove,
}: {
  deal: DealDrawerData
  submitting: boolean
  onAdd: (contactId: number, role?: string) => void
  onMakePrimary: (contactId: number) => void
  onRemove: (contactId: number) => void
}) {
  const { t } = useLingui()
  const contacts = deal.contacts ?? []
  const candidates = deal.companyContacts ?? []
  const [query, setQuery] = useState("")

  const filteredCandidates = useMemo(() => {
    const trimmed = query.trim().toLowerCase()
    if (!trimmed) return candidates
    return candidates.filter(
      (c) =>
        c.displayName.toLowerCase().includes(trimmed) ||
        (c.jobTitle ?? "").toLowerCase().includes(trimmed),
    )
  }, [query, candidates])

  const companyName = deal.company?.name
  const addSectionLabel = companyName
    ? t`Add from ${companyName}`
    : t`Add contact`

  return (
    <div
      className={`flex flex-col overflow-y-auto max-h-[60vh] ${
        submitting ? "pointer-events-none opacity-60" : ""
      }`}
    >
      {/* Search */}
      <div className="flex items-center py-3 px-3.5 gap-2 border-b border-[#EBE5D8]">
        <SearchIcon />
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            companyName
              ? t`Search contacts at ${companyName}…`
              : t`Search contacts…`
          }
          data-testid="contact-picker-search"
          className="grow bg-transparent text-[13px] leading-4 text-foreground placeholder:text-[#9A938A] outline-none"
        />
        <span
          aria-hidden="true"
          className="text-[#9A938A] text-[10px] leading-3"
        >
          esc
        </span>
      </div>

      {/* ON THIS DEAL section */}
      {contacts.length > 0 ? (
        <>
          <SectionHeader>
            <Trans>On this deal</Trans>
          </SectionHeader>
          {contacts.map((c) => (
            <RosterPickerRow
              key={c.id}
              contact={c}
              onMakePrimary={() => onMakePrimary(c.id)}
              onRemove={() => onRemove(c.id)}
            />
          ))}
        </>
      ) : null}

      {/* ADD FROM <Company> section */}
      {candidates.length > 0 ? (
        <SectionHeader>{addSectionLabel}</SectionHeader>
      ) : null}
      {filteredCandidates.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onAdd(c.id)}
          data-testid={`contact-picker-candidate-${c.id}`}
          className="flex items-center py-2 px-3.5 gap-2.5 text-left cursor-pointer hover:bg-[#FAF3EB]"
        >
          <ContactAvatar initials={c.initials} tone="candidate" />
          <div className="grow min-w-0 flex flex-col gap-px">
            <div className="text-foreground text-[13px] leading-4 truncate">
              {c.displayName}
            </div>
            {c.jobTitle ? (
              <div className="text-[#7A746B] text-[11px] leading-3.5 truncate">
                {c.jobTitle}
              </div>
            ) : null}
          </div>
          <span className="shrink-0 text-[#B86A2D] text-[11px] leading-3.5 font-medium">
            <Trans>+ Add</Trans>
          </span>
        </button>
      ))}
      {candidates.length > 0 && filteredCandidates.length === 0 ? (
        <div className="px-3.5 py-3 text-[#7A746B] text-xs leading-4">
          <Trans>No matches. Try a different name.</Trans>
        </div>
      ) : null}

      {/* Create new contact footer (deferred) */}
      <div className="h-px bg-[#EBE5D8] mt-1.5" />
      <button
        type="button"
        disabled
        aria-label={t`Create new contact (coming soon)`}
        data-testid="contact-picker-add-new"
        className="flex items-center py-3 px-3.5 gap-2.5 cursor-not-allowed opacity-60"
      >
        <span className="flex items-center justify-center shrink-0 rounded-full size-7 border border-dashed border-[#C9C2B6]">
          <PlusIcon />
        </span>
        <span className="text-foreground text-[13px] leading-4">
          <Trans>Create new contact…</Trans>
        </span>
      </button>
    </div>
  )
}

// Row for a contact already on the deal (inside the picker).
function RosterPickerRow({
  contact,
  onMakePrimary,
  onRemove,
}: {
  contact: DealDrawerRosterContact
  onMakePrimary: () => void
  onRemove: () => void
}) {
  const { t } = useLingui()
  return (
    <div
      data-testid={`contact-picker-roster-${contact.id}`}
      className="flex items-center py-2 px-3.5 gap-2.5 bg-[#FAF3EB]"
    >
      <ContactAvatar initials={contact.initials} tone="current" />
      <div className="grow min-w-0 flex flex-col gap-px">
        <div className="font-semibold text-foreground text-[13px] leading-4 truncate">
          {contact.displayName}
        </div>
        {contact.jobTitle ? (
          <div className="text-[#7A746B] text-[11px] leading-3.5 truncate">
            {contact.jobTitle}
          </div>
        ) : null}
      </div>

      {/* Primary star toggle */}
      <button
        type="button"
        onClick={contact.primary ? undefined : onMakePrimary}
        disabled={contact.primary}
        data-testid={`contact-picker-primary-${contact.id}`}
        aria-label={
          contact.primary
            ? t`Primary contact`
            : t`Make primary`
        }
        className={`shrink-0 flex items-center justify-center rounded-full size-6 ${
          contact.primary
            ? "text-[#B86A2D] cursor-default"
            : "text-[#C9C2B6] hover:text-[#B86A2D] cursor-pointer"
        }`}
      >
        <StarPickerIcon filled={contact.primary} />
      </button>

      {/* Remove button (hidden on primary) */}
      {!contact.primary ? (
        <button
          type="button"
          onClick={onRemove}
          data-testid={`contact-picker-remove-${contact.id}`}
          aria-label={t`Remove ${contact.displayName} from deal`}
          className="shrink-0 flex items-center justify-center rounded-full size-6 text-[#C9C2B6] hover:text-[#B84F4F] cursor-pointer"
        >
          <XIcon />
        </button>
      ) : null}
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <div className="pt-2.5 pb-1 px-3.5">
      <div className="tracking-[0.08em] uppercase font-semibold text-[#9A938A] text-[10px] leading-3">
        {children}
      </div>
    </div>
  )
}

function ContactAvatar({
  initials,
  tone,
}: {
  initials: string
  tone: "current" | "candidate"
}) {
  const className =
    tone === "current"
      ? "bg-[#E5DFD3] text-foreground"
      : "bg-[#C9C2B6] text-white"
  return (
    <span
      className={`flex items-center justify-center shrink-0 rounded-full size-7 font-semibold text-[11px] leading-3.5 ${className}`}
    >
      {initials}
    </span>
  )
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <circle cx="7" cy="7" r="4.5" stroke="#7A746B" strokeWidth="1.4" />
      <path
        d="M10.5 10.5L13 13"
        stroke="#7A746B"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M8 3V13M3 8H13"
        stroke="#7A746B"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function StarPickerIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill={filled ? "#B86A2D" : "none"}
      aria-hidden="true"
    >
      <path
        d="M8 1.5L9.9 5.9L14.5 6.3L11.2 9.1L12.3 13.6L8 11L3.7 13.6L4.8 9.1L1.5 6.3L6.1 5.9L8 1.5Z"
        stroke={filled ? "#B86A2D" : "currentColor"}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function XIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}
